export class HttpError extends Error {
  private httpStatusCode: number;
  private errorType?: string;

  constructor(httpStatusCode: number, underlyingErrorMessage?: string, errorType?: string) {
    super(underlyingErrorMessage);
    Object.setPrototypeOf(this, HttpError.prototype);
    this.httpStatusCode = httpStatusCode;
    this.errorType = errorType;
  }

  public getStatusCode(): number {
    return this.httpStatusCode;
  }

  public getErrorType(): string | undefined {
    return this.errorType;
  }
}

export class UnauthorizedError extends Error {
  constructor() {
    super('Unauthorized');
    Object.setPrototypeOf(this, UnauthorizedError.prototype);
  }
}

export class NotFoundError extends Error {
  constructor() {
    super('Not found');
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

export class NotOnboardedError extends Error {
  constructor() {
    super('Not onboarded');
    Object.setPrototypeOf(this, NotOnboardedError.prototype);
  }
}

export class InvalidPaymentAmountError extends Error {
  private minimumPaymentAmount: number;

  constructor(minimumPaymentAmount: number) {
    super(`Invalid payment amount. Minimum payment amount is ${minimumPaymentAmount}`);
    Object.setPrototypeOf(this, InvalidPaymentAmountError.prototype);
    this.minimumPaymentAmount = minimumPaymentAmount;
  }

  getMinimumPaymentAmount(): number {
    return this.minimumPaymentAmount;
  }
}

export class AccountNotSelectedError extends Error {
  constructor() {
    super('Account not selected');
    Object.setPrototypeOf(this, AccountNotSelectedError.prototype);
  }
}

export class PaymentTypeNotSelectedError extends Error {
  constructor() {
    super('Payment type is not selected');
    Object.setPrototypeOf(this, PaymentTypeNotSelectedError.prototype);
  }
}

export class PaymentAmountNotSelectedError extends Error {
  constructor() {
    super('Payment amount is not selected');
    Object.setPrototypeOf(this, PaymentAmountNotSelectedError.prototype);
  }
}

export class PaymentAmountTooLowError extends Error {
  constructor() {
    super('Payment amount is too low');
    Object.setPrototypeOf(this, PaymentAmountTooLowError.prototype);
  }
}

export class NoLeaseSelectedError extends Error {
  constructor() {
    super('No lease selected');
    Object.setPrototypeOf(this, NoLeaseSelectedError.prototype);
  }
}

export class NoPaymentOptionsError extends Error {
  constructor() {
    super('No payment options found');
    Object.setPrototypeOf(this, NoPaymentOptionsError.prototype);
  }
}

export class UnknownError extends Error {
  constructor() {
    super('Unknown error');
    Object.setPrototypeOf(this, UnknownError.prototype);
  }
}

export class InvalidUserNameAndPasswordError extends Error {
  constructor() {
    super('Invalid user name and password');
    Object.setPrototypeOf(this, InvalidUserNameAndPasswordError.prototype);
  }
}

export class PayloadTooLargeError extends Error {
  constructor() {
    super('Payload too large');
    Object.setPrototypeOf(this, PayloadTooLargeError.prototype);
  }
}

export class InsurancePolicyFileRequiredError extends Error {
  constructor() {
    super('Insurance policy file is required');
    Object.setPrototypeOf(this, InsurancePolicyFileRequiredError.prototype);
  }
}

export class MessageDeliveryFailedError extends Error {
  constructor() {
    super('Message delivery failed');
    Object.setPrototypeOf(this, MessageDeliveryFailedError.prototype);
  }
}

export class LeaseCannotAcceptPaymentsError extends Error {
  constructor() {
    super('Lease cannot accept payments');
    Object.setPrototypeOf(this, LeaseCannotAcceptPaymentsError.prototype);
  }
}

export class CannotMakeCardPaymentError extends Error {
  constructor() {
    super('Cannot make card payment');
    Object.setPrototypeOf(this, CannotMakeCardPaymentError.prototype);
  }
}

export class CannotMakeAchPaymentError extends Error {
  constructor() {
    super('Cannot make ach payment');
    Object.setPrototypeOf(this, CannotMakeAchPaymentError.prototype);
  }
}

export class NoAutopaymentSelectedError extends Error {
  constructor() {
    super('No autopayment selected');
    Object.setPrototypeOf(this, NoAutopaymentSelectedError.prototype);
  }
}

export class AutopaymentFailedError extends Error {
  constructor() {
    super('Autopayment failed');
    Object.setPrototypeOf(this, AutopaymentFailedError.prototype);
  }
}

export class NoMaintenanceRequestIdError extends Error {
  constructor() {
    super('No maintenance request id');
    Object.setPrototypeOf(this, NoMaintenanceRequestIdError.prototype);
  }
}

export class NoLeasesError extends Error {
  constructor() {
    super('No leases found');
    Object.setPrototypeOf(this, NoLeasesError.prototype);
  }
}

export class NoTransactionIdError extends Error {
  constructor() {
    super('No transaction id');
    Object.setPrototypeOf(this, NoTransactionIdError.prototype);
  }
}

export class NoTransactionRecordError extends Error {
  constructor() {
    super('No transaction record');
    Object.setPrototypeOf(this, NoTransactionRecordError.prototype);
  }
}

export class NoChatsError extends Error {
  constructor() {
    super('No chats found');
    Object.setPrototypeOf(this, NoChatsError.prototype);
  }
}

export class NoLeaseRenewalOfferError extends Error {
  constructor() {
    super('No lease renewal offer found');
    Object.setPrototypeOf(this, NoLeaseRenewalOfferError.prototype);
  }
}

export class NoChatSelectedError extends Error {
  constructor() {
    super('No chat selected');
    Object.setPrototypeOf(this, NoChatSelectedError.prototype);
  }
}

export class FailedToSubscribeToChatMessagesError extends Error {
  constructor() {
    super('Failed to subscribe to chat messages');
    Object.setPrototypeOf(this, FailedToSubscribeToChatMessagesError.prototype);
  }
}

export class FailedToUnsubscribeFromChatMessagesError extends Error {
  constructor() {
    super('Failed to unsubscribe from chat messages');
    Object.setPrototypeOf(this, FailedToUnsubscribeFromChatMessagesError.prototype);
  }
}

export class NoMaintenanceRequestError extends Error {
  constructor() {
    super('No maintenance request');
    Object.setPrototypeOf(this, NoMaintenanceRequestError.prototype);
  }
}

export class NoMaintenanceRequestDescriptionError extends Error {
  constructor() {
    super('No maintenance request description');
    Object.setPrototypeOf(this, NoMaintenanceRequestDescriptionError.prototype);
  }
}

export class NoAutoPaymentIdError extends Error {
  constructor() {
    super('No auto payment id');
    Object.setPrototypeOf(this, NoAutoPaymentIdError.prototype);
  }
}

export class NoAccountIdError extends Error {
  constructor() {
    super('No account id');
    Object.setPrototypeOf(this, NoAccountIdError.prototype);
  }
}

export class BankAccountNotLinkedError extends Error {
  constructor() {
    super('Bank account not linked');
    Object.setPrototypeOf(this, BankAccountNotLinkedError.prototype);
  }
}

export class InsufficientFundsError extends Error {
  constructor() {
    super('Insufficient funds');
    Object.setPrototypeOf(this, InsufficientFundsError.prototype);
  }
}

export class TenantAccountNotMappedError extends Error {
  constructor() {
    super('Tenant account not mapped');
    Object.setPrototypeOf(this, TenantAccountNotMappedError.prototype);
  }
}

export class TransferLimitReachedError extends Error {
  constructor() {
    super('Transfer limit reached');
    Object.setPrototypeOf(this, TransferLimitReachedError.prototype);
  }
}

export class AccountBlockedError extends Error {
  constructor() {
    super('Account blocked');
    Object.setPrototypeOf(this, AccountBlockedError.prototype);
  }
}

export class CardLostError extends Error {
  constructor() {
    super('Card lost');
    Object.setPrototypeOf(this, CardLostError.prototype);
  }
}

export class StolenCardError extends Error {
  constructor() {
    super('Stolen card');
    Object.setPrototypeOf(this, StolenCardError.prototype);
  }
}

export class ExpiredCardError extends Error {
  constructor() {
    super('Expired card');
    Object.setPrototypeOf(this, ExpiredCardError.prototype);
  }
}

export class DuplicationError extends Error {
  constructor() {
    super('Duplication');
    Object.setPrototypeOf(this, DuplicationError.prototype);
  }
}

export class PaymentAmountOverRequestedError extends Error {
  constructor() {
    super('Payment amount over requested');
    Object.setPrototypeOf(this, PaymentAmountOverRequestedError.prototype);
  }
}

export class PaymentsDisabledError extends Error {
  constructor() {
    super('Payments disabled');
    Object.setPrototypeOf(this, PaymentsDisabledError.prototype);
  }
}

export class FailedToConnectError extends Error {
  constructor() {
    super('Failed to connect');
    Object.setPrototypeOf(this, FailedToConnectError.prototype);
  }
}

export class LeaseIdRequiredError extends Error {
  constructor() {
    super('Lease ID is required');
    Object.setPrototypeOf(this, LeaseIdRequiredError.prototype);
  }
}

export class VerificationCodeSentTooFrequentlyError extends Error {
  constructor() {
    super('Verification code sent too frequently');
    Object.setPrototypeOf(this, VerificationCodeSentTooFrequentlyError.prototype);
  }
}

export class MagicLinkTokenExpiredError extends Error {
  constructor() {
    super('Magic link token expired');
    Object.setPrototypeOf(this, MagicLinkTokenExpiredError.prototype);
  }
}

export class VerificationCodeExpiredError extends Error {
  constructor() {
    super('Verification code expired');
    Object.setPrototypeOf(this, VerificationCodeExpiredError.prototype);
  }
}

export class PhoneNumberIsRequiredForPhoneVerificationError extends Error {
  constructor() {
    super('Phone number is required for phone verification');
    Object.setPrototypeOf(this, PhoneNumberIsRequiredForPhoneVerificationError.prototype);
  }
}

export class NoMagicLinkTokenError extends Error {
  constructor() {
    super('No magic link token');
    Object.setPrototypeOf(this, NoMagicLinkTokenError.prototype);
  }
}

export class NoPhoneVerificationCodeError extends Error {
  constructor() {
    super('No phone verification code');
    Object.setPrototypeOf(this, NoPhoneVerificationCodeError.prototype);
  }
}

export class EmailIsRequiredForMagicLinkError extends Error {
  constructor() {
    super('Email is required for magic link');
    Object.setPrototypeOf(this, EmailIsRequiredForMagicLinkError.prototype);
  }
}

export class InvalidPhoneVerificationCodeLengthError extends Error {
  constructor() {
    super('Invalid phone verification code length');
    Object.setPrototypeOf(this, InvalidPhoneVerificationCodeLengthError.prototype);
  }
}
